/**
 * Treppan Academy - Courses List
 * Grid/List view of all available courses with filters
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen, Clock, Filter, Grid, List, Search,
  Trophy, Target, ChevronRight, GraduationCap
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  success: '#10B981',
  fail: '#EF4444',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const modules = [
  { value: 'all', label: 'All Modules' },
  { value: 'lead_management', label: 'Lead Management' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'automation', label: 'Automation' },
  { value: 'communication', label: 'Communication' },
  { value: 'real_estate', label: 'Real Estate' }
];

const statusFilters = [
  { value: 'all', label: 'All Status' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

const CourseCard = ({ course, onClick, viewMode }) => {
  const progress = course.user_progress?.progress_percent || 0;
  const status = course.user_progress?.status || 'not_started';
  
  const statusColors = {
    not_started: colors.textMuted,
    in_progress: colors.gold,
    completed: colors.success
  };
  
  const statusLabels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed'
  };

  if (viewMode === 'list') {
    return (
      <div 
        className="flex items-center gap-6 p-4 rounded-xl cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        onClick={onClick}
        data-testid={`course-list-item-${course.id}`}
      >
        <img 
          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=200'}
          alt={course.title}
          className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 
              className="font-semibold truncate"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              {course.title}
            </h3>
            <Badge 
              className="uppercase text-xs tracking-widest flex-shrink-0"
              style={{ 
                backgroundColor: `${statusColors[status]}20`,
                color: statusColors[status],
                border: `1px solid ${statusColors[status]}30`
              }}
            >
              {statusLabels[status]}
            </Badge>
          </div>
          <p className="text-sm truncate" style={{ color: colors.textBody }}>
            {course.description}
          </p>
        </div>
        
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Duration</p>
            <p className="font-medium" style={{ color: colors.textHeading }}>{course.duration_hours}h</p>
          </div>
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Lessons</p>
            <p className="font-medium" style={{ color: colors.textHeading }}>{course.lessons?.length || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Progress</p>
            <p className="font-medium" style={{ color: statusColors[status] }}>{progress}%</p>
          </div>
          <ChevronRight className="h-5 w-5" style={{ color: colors.textMuted }} />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      onClick={onClick}
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative h-40 overflow-hidden">
        <img 
          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'} 
          alt={course.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        
        <div className="absolute top-3 right-3 flex gap-2">
          <Badge 
            className="uppercase text-xs tracking-widest"
            style={{ 
              backgroundColor: course.difficulty === 'advanced' ? `${colors.gold}90` : course.difficulty === 'intermediate' ? `${colors.success}90` : `rgba(255,255,255,0.9)`,
              color: course.difficulty === 'advanced' ? '#000' : course.difficulty === 'intermediate' ? '#000' : '#000'
            }}
          >
            {course.difficulty}
          </Badge>
        </div>
        
        {status !== 'not_started' && (
          <div className="absolute bottom-3 left-3 right-3">
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: status === 'completed' ? colors.success : colors.gold
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Badge 
            variant="outline"
            className="text-xs"
            style={{ 
              backgroundColor: `${statusColors[status]}15`,
              color: statusColors[status],
              borderColor: `${statusColors[status]}30`
            }}
          >
            {status === 'completed' && <Trophy className="h-3 w-3 mr-1" />}
            {status === 'in_progress' && <Target className="h-3 w-3 mr-1" />}
            {statusLabels[status]}
          </Badge>
        </div>
        
        <h3 
          className="text-lg font-semibold mb-2 line-clamp-1"
          style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
        >
          {course.title}
        </h3>
        <p className="text-sm line-clamp-2 mb-4" style={{ color: colors.textBody }}>
          {course.description}
        </p>
        
        <div className="flex items-center gap-4 text-sm" style={{ color: colors.textMuted }}>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {course.duration_hours}h
          </span>
          <span className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            {course.lessons?.length || 0} lessons
          </span>
        </div>
      </div>
    </div>
  );
};

export default function CoursesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState(searchParams.get('module') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/courses?token=${token}`);
      const result = await res.json();
      
      if (result.success) {
        setCourses(result.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    // Search filter
    if (searchQuery && !course.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Module filter
    if (moduleFilter !== 'all' && course.module !== moduleFilter) {
      return false;
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      const status = course.user_progress?.status || 'not_started';
      if (status !== statusFilter) {
        return false;
      }
    }
    
    return true;
  });

  const stats = {
    total: courses.length,
    completed: courses.filter(c => c.user_progress?.status === 'completed').length,
    inProgress: courses.filter(c => c.user_progress?.status === 'in_progress').length,
    notStarted: courses.filter(c => !c.user_progress || c.user_progress.status === 'not_started').length
  };

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/academy')}
            style={{ color: colors.textMuted }}
          >
            <GraduationCap className="h-4 w-4 mr-1" />
            Academy
          </Button>
          <span style={{ color: colors.textMuted }}>/</span>
          <span style={{ color: colors.textHeading }}>Courses</span>
        </div>
        <h1 
          className="text-3xl font-bold tracking-tight"
          style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
        >
          Course Catalog
        </h1>
        <p style={{ color: colors.textBody }}>
          Browse and enroll in courses to master Treppan CRM
        </p>
      </div>

      {/* Stats Bar */}
      <div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl mb-6"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <div className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.textHeading }}>{stats.total}</p>
          <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Total Courses</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.success }}>{stats.completed}</p>
          <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Completed</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.gold }}>{stats.inProgress}</p>
          <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>In Progress</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold" style={{ color: colors.textHeading }}>{stats.notStarted}</p>
          <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Not Started</p>
        </div>
      </div>

      {/* Filters */}
      <div 
        className="flex flex-wrap items-center gap-4 p-4 rounded-xl mb-6"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.textMuted }} />
          <Input 
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            style={{ backgroundColor: colors.background, borderColor: colors.border, color: colors.textHeading }}
            data-testid="search-courses"
          />
        </div>
        
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-[180px]" style={{ backgroundColor: colors.background, borderColor: colors.border, color: colors.textHeading }}>
            <Filter className="h-4 w-4 mr-2" style={{ color: colors.textMuted }} />
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
            {modules.map(m => (
              <SelectItem key={m.value} value={m.value} style={{ color: colors.textHeading }}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" style={{ backgroundColor: colors.background, borderColor: colors.border, color: colors.textHeading }}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
            {statusFilters.map(s => (
              <SelectItem key={s.value} value={s.value} style={{ color: colors.textHeading }}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex items-center gap-2 ml-auto">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setViewMode('grid')}
            style={{ 
              color: viewMode === 'grid' ? colors.gold : colors.textMuted,
              backgroundColor: viewMode === 'grid' ? `${colors.gold}15` : 'transparent'
            }}
            data-testid="view-grid"
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setViewMode('list')}
            style={{ 
              color: viewMode === 'list' ? colors.gold : colors.textMuted,
              backgroundColor: viewMode === 'list' ? `${colors.gold}15` : 'transparent'
            }}
            data-testid="view-list"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Course Grid/List */}
      {loading ? (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading courses...</p>
        </div>
      ) : filteredCourses.length > 0 ? (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
          : 'space-y-4'
        }>
          {filteredCourses.map(course => (
            <CourseCard 
              key={course.id}
              course={course}
              viewMode={viewMode}
              onClick={() => navigate(`/academy/courses/${course.id}`)}
            />
          ))}
        </div>
      ) : (
        <div 
          className="text-center py-12 rounded-xl"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <BookOpen className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
          <p className="font-semibold mb-2" style={{ color: colors.textHeading }}>No courses found</p>
          <p style={{ color: colors.textMuted }}>
            {searchQuery || moduleFilter !== 'all' || statusFilter !== 'all' 
              ? 'Try adjusting your filters'
              : 'No courses available yet'}
          </p>
        </div>
      )}
    </div>
  );
}
